import { createSlice } from '@reduxjs/toolkit';
import { BASE_END_POINT_COMPANY } from '../../../config';
import { fetchCandidateApplications } from '../applications/applicationsSlice';

const initialState = {
  sections: [],
  loading: false,
  error: null,
  fetched: false
};

const suggestionsSlice = createSlice({
  name: 'suggestions',
  initialState,
  reducers: {
    fetchSuggestionsRequest(state) {
      state.loading = true;
      state.error = null;
    },
    fetchSuggestionsSuccess(state, action) {
      state.loading = false;
      state.sections = action.payload.sections;
      state.fetched = true;
    },
    fetchSuggestionsFailure(state, action) {
      state.loading = false;
      state.error = action.payload.error;
      state.fetched = false;
    },
    clearSuggestions(state) {
      state.sections = [];
      state.loading = false;
      state.error = null;
      state.fetched = false;
    }
  }
});

export const {
  fetchSuggestionsRequest,
  fetchSuggestionsSuccess,
  fetchSuggestionsFailure,
  clearSuggestions
} = suggestionsSlice.actions;

export const suggestionsSectionsSelector = (state) => state.suggestions.sections;
export const suggestionsLoadingSelector = (state) => state.suggestions.loading;
export const suggestionsErrorSelector = (state) => state.suggestions.error;
export const suggestionsFetchedSelector = (state) => state.suggestions.fetched;

const getTopCategories = (jobs, maxCategories = 2) => {
  const counts = jobs.reduce((acc, job) => {
    if (job?.category) {
      acc[job.category] = (acc[job.category] || 0) + 1;
    }
    return acc;
  }, {});

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxCategories)
    .map(([category]) => category);
};

const fetchJobsByIds = async (jobIds) => {
  const params = new URLSearchParams();
  params.append('ids', jobIds.join(','));
  const url = `${BASE_END_POINT_COMPANY}/api/v1/job/batch?${params.toString()}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = await response.json();
  return Array.isArray(data?.data) ? data.data : [];
};

const fetchActiveJobsByCategory = async (category, size = 8) => {
  const params = new URLSearchParams();
  params.append('category', category);
  params.append('page', 0);
  params.append('size', size);
  const url = `${BASE_END_POINT_COMPANY}/api/v1/job/active?${params.toString()}`;
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }
  const data = await response.json();
  return Array.isArray(data?.data) ? data.data : [];
};

export const fetchSuggestions = () => async (dispatch, getState) => {
  dispatch(fetchSuggestionsRequest());

  try {
    const state = getState();
    const applicationsState = state.applications;
    let applications = applicationsState.candidateApplications || [];

    if (!applicationsState.candidateFetched) {
      const result = await dispatch(fetchCandidateApplications());
      if (result?.payload?.applications) {
        applications = result.payload.applications;
      } else if (result?.applications) {
        applications = result.applications;
      }
    }

    const appliedJobIds = Array.from(
      new Set(applications.map((app) => app.jobId).filter(Boolean))
    );

    if (appliedJobIds.length === 0) {
      dispatch(fetchSuggestionsSuccess({ sections: [] }));
      return { success: true, sections: [] };
    }

    const appliedJobs = await fetchJobsByIds(appliedJobIds);
    const topCategories = getTopCategories(appliedJobs);

    if (topCategories.length === 0) {
      dispatch(fetchSuggestionsSuccess({ sections: [] }));
      return { success: true, sections: [] };
    }

    const sections = [];
    const seenJobIds = new Set(appliedJobIds);

    for (const category of topCategories) {
      const results = await fetchActiveJobsByCategory(category);
      const filtered = results.filter((jobResult) => {
        const jobId = jobResult?.job?.id;
        if (!jobId || seenJobIds.has(jobId)) {
          return false;
        }
        seenJobIds.add(jobId);
        return true;
      });

      if (filtered.length > 0) {
        sections.push({
          category,
          jobs: filtered.slice(0, 6)
        });
      }
    }

    dispatch(fetchSuggestionsSuccess({ sections }));
    return { success: true, sections };
  } catch (error) {
    dispatch(fetchSuggestionsFailure({ error: error.message }));
    return { success: false, error: error.message };
  }
};

export default suggestionsSlice.reducer;
